// src/components/InexistenciaSolicitudes.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiConfig } from "../config/api.config";
import BackToMenu from "./BackToMenu.jsx";
import logoCopacabana from "../assets/logocopacabana.png";
import { Calendar, Edit, FileText, LogOut, RefreshCw, Search, User, X } from "lucide-react";

const ROLE_MAP = {
  1: "solicitante",
  2: "planeacion",
  3: "hacienda",
  4: "juridica",
  5: "alcalde",
  6: "administrador",
  7: "presupuesto",
  8: "almacen",
  9: "validador",
  10: "secretario",
};

const ROLE_LABELS = {
  solicitante: "Solicitante",
  planeacion: "Planeación",
  hacienda: "Hacienda",
  juridica: "Jurídica",
  alcalde: "Alcalde",
  administrador: "Administrador",
  presupuesto: "Presupuesto",
  almacen: "Almacén",
  validador: "Validador",
  secretario: "Secretario",
};

const normalizeStatus = (status) => (status || "").toString().trim().toLowerCase().replace(/\s+/g, "_");

const getStatusBadgeStyle = (status) => {
  const s = normalizeStatus(status);
  switch (s) {
    case "en_proceso":
    case "en_tramite":
    case "en_trámite":
    case "pendiente":
    case "pending":
      return { backgroundColor: "#fef9c3", color: "#854d0e" };
    case "aprobado":
    case "finalizado":
    case "approved":
      return { backgroundColor: "#dcfce7", color: "#166534" };
    case "rechazado":
    case "rejected":
      return { backgroundColor: "#fee2e2", color: "#b91c1c" };
    default:
      return { backgroundColor: "#f3f4f6", color: "#374151" };
  }
};

const getStatusLabel = (status) => {
  const s = normalizeStatus(status);
  switch (s) {
    case "en_proceso":
    case "en_tramite":
    case "en_trámite":
    case "pending":
    case "pendiente":
      return "En proceso";
    case "aprobado":
    case "approved":
    case "finalizado":
      return "Finalizado";
    case "rechazado":
    case "rejected":
      return "Rechazado";
    default:
      return status || "Desconocido";
  }
};

// ✅ Nueva lógica de fase adaptada a current_step (9, 10, 11)
const getPhaseText = (step, status) => {
  const s = normalizeStatus(status);

  // Si está finalizado
  if (s === "aprobado" || s === "finalizado") {
    return "Certificado finalizado";
  }

  // Si está rechazado
  if (s === "rechazado") {
    return "Rechazado";
  }

  // Fases según current_step
  const stepNum = Number(step);
  switch (stepNum) {
    case 9:
      return "Pendiente firma Validador";
    case 10:
      return "Pendiente firma Secretario";
    case 11:
    default:
      return "Certificado finalizado";
  }
};

const safeParseJSON = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function InexistenciaSolicitudes({ role }) {
  const navigate = useNavigate();

  const [windowSize, setWindowSize] = useState({ width: window.innerWidth });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchVisible, setSearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [solicitudes, setSolicitudes] = useState([]);
  const [contractsById, setContractsById] = useState({});
  const [contractsByRadicado, setContractsByRadicado] = useState({});

  const storedUser = safeParseJSON(localStorage.getItem("auth_user"));
  const storedRoleName = (storedUser?.role ?? storedUser?.roleName ?? storedUser?.rol ?? "").toString().trim();
  const storedRoleId =
    storedUser?.role_id ?? storedUser?.rolId ?? storedUser?.roleId ?? storedUser?.rol_id ?? null;

  const effectiveRoleRaw = role || storedRoleName || (storedRoleId != null ? String(storedRoleId) : "");
  const roleKey = ROLE_MAP[Number(effectiveRoleRaw)] || effectiveRoleRaw || "";
  const roleLabel = ROLE_LABELS[roleKey] || roleKey || "Usuario";
  const email = storedUser?.email || storedUser?.correo || "-";

  useEffect(() => {
    const onResize = () => setWindowSize({ width: window.innerWidth });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = windowSize.width < 768;

  const getColumnWidths = () => {
    if (isMobile) {
      return {
        fecha: "10%",
        secretaria: "12%",
        radicado: "8%",
        contratista: "14%",
        estado: "8%",
        fase: "14%",
        acciones: "12%",
      };
    }
    return {
      fecha: "10%",
      secretaria: "12%",
      radicado: "8%",
      contratista: "14%",
      estado: "8%",
      fase: "14%",
      acciones: "12%",
    };
  };

  const columnWidths = getColumnWidths();

  const fetchAll = async () => {
    setLoading(true);
    setError("");

    try {
      // 1) solicitudes inexistencia
      const baseSolic = apiConfig.getUrl(
        apiConfig.endpoints?.inexistenciaSolicitudes || "/api/inexistencia/solicitudes",
      );
      const urlSolic = effectiveRoleRaw ? `${baseSolic}?role=${encodeURIComponent(effectiveRoleRaw)}` : baseSolic;

      const respSolic = await fetch(urlSolic, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (!respSolic.ok) {
        const txt = await respSolic.text().catch(() => "");
        throw new Error(`Error cargando solicitudes (${respSolic.status}) ${txt}`);
      }

      const dataSolic = await respSolic.json().catch(() => []);
      const listSolic = Array.isArray(dataSolic) ? dataSolic : [];
      
      // ✅ Filtrar solo las que NO estén finalizadas (current_step < 11)
      const filteredSolic = listSolic.filter(s => {
        const step = Number(s?.current_step ?? 0);
        const status = normalizeStatus(s?.status ?? "");
        
        // Mostrar si: step <= 10 (validador o secretario) Y no está finalizado
        return step <= 10 && status === "pending";
      });
      
      setSolicitudes(filteredSolic);

      // 2) contratos
      const urlContracts = apiConfig.getUrl(apiConfig.endpoints?.contracts || "/api/contracts");
      const respC = await fetch(urlContracts, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (!respC.ok) {
        const txt = await respC.text().catch(() => "");
        throw new Error(`Error cargando contratos (${respC.status}) ${txt}`);
      }

      const dataC = await respC.json().catch(() => []);
      const listC = Array.isArray(dataC) ? dataC : [];

      const byId = {};
      const byRad = {};
      for (const c of listC) {
        if (!c) continue;
        const id = c.id;
        const rad = (c.radicado || c.numero_radicado || "").toString().trim();
        if (id) byId[id] = c;
        if (rad) byRad[rad] = c;
      }
      setContractsById(byId);
      setContractsByRadicado(byRad);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Error cargando datos");
      setSolicitudes([]);
      setContractsById({});
      setContractsByRadicado({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRoleRaw]);

  const rows = useMemo(() => {
    return solicitudes.map((s) => {
      const solicitudId = s?.id ?? null;

      const radicado = (s?.radicado || s?.radicado_contrato || "").toString().trim();
      const contractId =
        s?.contract_id || s?.contrato_id || s?.contractId || s?.contratoId || s?.id_contrato || null;

      const contract =
        (contractId && contractsById[contractId]) ||
        (radicado && contractsByRadicado[radicado]) ||
        s?.contract ||
        null;

      const fecha =
        contract?.fecha_solicitud || contract?.created_at || s?.sent_at || s?.created_at || null;

      return {
        solicitudId,
        contractId: contract?.id || contractId || null,
        contract,
        fecha,
        secretaria: contract?.secretaria || s?.secretaria || "-",
        radicado: contract?.radicado || radicado || "-",
        nombre_contratista:
          contract?.nombre_contratista || contract?.contratista || s?.nombre_contratista || "-",
        status: contract?.status || contract?.estado || s?.status || s?.estado || "",
        current_step: s?.current_step ?? null,  // ✅ Usar current_step
      };
    });
  }, [solicitudes, contractsById, contractsByRadicado]);

  const filtered = useMemo(() => {
    const t = searchTerm.trim().toLowerCase();
    if (!t) return rows;

    return rows.filter((r) => {
      const faseTxt = getPhaseText(Number(r.current_step), r.status);
      const haystack = `${r.radicado} ${r.secretaria} ${r.nombre_contratista} ${getStatusLabel(
        r.status,
      )} ${faseTxt}`.toLowerCase();
      return haystack.includes(t);
    });
  }, [rows, searchTerm]);

  const handleLogout = () => {
    navigate("/logout");
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(to bottom, #1e3a8a, #1e40af)",
        }}
      >
        <div style={{ color: "white", fontSize: "1.25rem", fontWeight: "bold" }}>Cargando…</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "linear-gradient(to bottom, #1e3a8a, #1e40af)",
        padding: isMobile ? "1rem" : "2rem",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
        <BackToMenu />

        <div
          style={{
            backgroundColor: "white",
            borderRadius: "1rem",
            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            overflow: "hidden",
            marginTop: "1rem",
          }}
        >
          <div style={{ textAlign: "center", padding: isMobile ? "1.5rem 1rem" : "2rem 1.5rem" }}>
            <img
              src={logoCopacabana}
              alt="Alcaldía de Copacabana"
              style={{ height: isMobile ? "80px" : "120px", margin: "0 auto" }}
            />
            <h1
              style={{
                marginTop: "1rem",
                fontSize: isMobile ? "1.5rem" : "2.25rem",
                fontWeight: "bold",
                color: "#1e40af",
              }}
            >
              Solicitudes de Inexistencia
            </h1>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "1rem",
              padding: "1rem",
              borderTop: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: "999px",
                padding: "0.5rem 1rem",
                fontSize: "0.875rem",
                color: "#1e3a8a",
                gap: "0.5rem",
              }}
            >
              <User size={16} />
              <span style={{ fontWeight: 600 }}>
                {email} ({roleLabel})
              </span>
            </div>

            <button
              onClick={handleLogout}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "999px",
                padding: "0.5rem 1rem",
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              }}
            >
              <LogOut size={16} />
              Cerrar sesión
            </button>
          </div>

          <div style={{ padding: isMobile ? "0.75rem" : "1rem", display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
            <button
              type="button"
              onClick={() => setSearchVisible((v) => !v)}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "999px",
                border: "1px solid #bfdbfe",
                backgroundColor: "#eff6ff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              title="Buscar"
            >
              {searchVisible ? <X size={18} color="#1e40af" /> : <Search size={18} color="#1e40af" />}
            </button>

            <button
              type="button"
              onClick={fetchAll}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.55rem 0.9rem",
                borderRadius: "0.75rem",
                border: "1px solid #e5e7eb",
                backgroundColor: "#f9fafb",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <RefreshCw size={16} />
              Refrescar
            </button>
          </div>

          {searchVisible && (
            <div style={{ padding: isMobile ? "0 0.75rem 0.75rem" : "0 1rem 1rem" }}>
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por radicado, secretaría, contratista, estado o fase…"
                style={{
                  width: "100%",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0.75rem",
                  padding: "0.75rem 1rem",
                  outline: "none",
                  fontSize: "0.95rem",
                }}
              />
            </div>
          )}

          <div style={{ padding: isMobile ? "0.75rem" : "1.5rem", width: "100%", boxSizing: "border-box" }}>
            {error ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "#b91c1c",
                  backgroundColor: "#fee2e2",
                  borderRadius: "0.5rem",
                }}
              >
                {error}
              </div>
            ) : filtered.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderRadius: "0.5rem",
                }}
              >
                <FileText size={48} style={{ margin: "0 auto 1rem", color: "#9ca3af" }} />
                <p>
                  {searchTerm ? "No se encontraron solicitudes que coincidan con la búsqueda" : "No hay solicitudes pendientes"}
                </p>
              </div>
            ) : (
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
                <table
                  style={{
                    width: "100%",
                    tableLayout: isMobile ? "auto" : "fixed",
                    borderCollapse: "separate",
                    borderSpacing: "0",
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    fontSize: isMobile ? "0.8rem" : "1rem",
                  }}
                >
                  <colgroup>
                    <col style={{ width: columnWidths.fecha }} />
                    <col style={{ width: columnWidths.secretaria }} />
                    <col style={{ width: columnWidths.radicado }} />
                    <col style={{ width: columnWidths.contratista }} />
                    <col style={{ width: columnWidths.estado }} />
                    <col style={{ width: columnWidths.fase }} />
                    <col style={{ width: columnWidths.acciones }} />
                  </colgroup>

                  <thead>
                    <tr style={{ backgroundColor: "#1e40af", color: "white" }}>
                      {["Fecha", "Secretaría", "Radicado", "Nombre del Contratista", "Estado", "Fase", "Acciones"].map(
                        (h, i) => (
                          <th
                            key={h}
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              textAlign: i === 6 ? "center" : "left",
                              fontSize: isMobile ? "0.7rem" : "0.75rem",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.map((r, idx) => {
                      const fechaStr = r.fecha ? new Date(r.fecha).toLocaleDateString() : "-";
                      const faseTxt = getPhaseText(Number(r.current_step), r.status);

                      return (
                        <tr key={r.solicitudId ?? idx} style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb" }}>
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
                              <Calendar size={14} color="#2563eb" />
                              {fechaStr}
                            </span>
                          </td>

                          <td
                            title={r.secretaria}
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.secretaria}
                          </td>

                          <td
                            title={r.radicado}
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <span
                              style={{
                                backgroundColor: "#eef2ff",
                                color: "#1e40af",
                                padding: "0.25rem 0.5rem",
                                borderRadius: "999px",
                                fontWeight: 600,
                                fontSize: isMobile ? "0.7rem" : "0.75rem",
                              }}
                            >
                              {r.radicado}
                            </span>
                          </td>

                          <td
                            title={r.nombre_contratista}
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.nombre_contratista}
                          </td>

                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                padding: "0.25rem 0.5rem",
                                borderRadius: "999px",
                                fontSize: isMobile ? "0.7rem" : "0.75rem",
                                fontWeight: 600,
                                ...getStatusBadgeStyle(r.status),
                              }}
                            >
                              {getStatusLabel(r.status)}
                            </span>
                          </td>

                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "normal",
                              lineHeight: "1.3",
                              maxWidth: isMobile ? "auto" : "150px",
                            }}
                          >
                            {faseTxt}
                          </td>

                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              textAlign: "center",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/inexistencia/${r.solicitudId}`, {
                                  state: {
                                    solicitudId: r.solicitudId,
                                    contractId: r.contractId,
                                    radicado: r.radicado,
                                  },
                                })
                              }
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#1e40af",
                                color: "white",
                                padding: isMobile ? "0.4rem 0.5rem" : "0.5rem 0.75rem",
                                borderRadius: "0.375rem",
                                border: "none",
                                fontSize: isMobile ? "0.7rem" : "0.75rem",
                                fontWeight: "500",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                minWidth: isMobile ? "auto" : "110px",
                              }}
                            >
                              <Edit size={14} style={{ marginRight: "0.25rem" }} />
                              Ver / firmar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div style={{ marginTop: "0.75rem", color: "#6b7280", fontSize: "0.85rem" }}>
                  Mostrando {filtered.length} de {rows.length} solicitudes pendientes.
                </div>
              </div>
            )}
          </div>

          <div style={{ textAlign: "center", padding: "1rem", borderTop: "1px solid #e5e7eb", color: "#6b7280" }}>
            <img src={logoCopacabana} alt="" style={{ height: "40px", opacity: 0.6 }} />
            <p style={{ marginTop: "0.5rem", fontSize: "0.875rem" }}>
              Sistema de Gestión de Contratos - Alcaldía Municipal
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}